/**
 * API Services pour l'authentification
 * RÉUTILISABILITÉ : 100% - TypeScript pur
 */

import { apiClient } from '@shared/infrastructure/http';
import { getAuthConfig } from '../../config/authConfig';
import type {
  ApiResponse,
  LoginCredentials,
  LoginResult,
  TwoFactorVerification,
  TwoFactorResult,
  TokenRefreshResult,
  UserProfile,
  UpdateProfileData,
  ChangePasswordData,
  ChangeEmailData,
  VerifyEmailChangeData,
  DeleteAccountData,
  DeleteAccountResult,
} from '../../application/dto/dtos';

/**
 * Service d'authentification
 */
export const authApi = {
  /**
   * Connexion utilisateur (email + mot de passe)
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<LoginResult>> {
    const config = getAuthConfig();
    try {
      const response = await apiClient.post<LoginResult>(config.endpoints.login, credentials, {
        requiresAuth: false,  // Pas besoin d'auth pour se connecter
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Vérification code 2FA
   */
  async verify2FA(data: TwoFactorVerification): Promise<ApiResponse<TwoFactorResult>> {
    const config = getAuthConfig();
    return apiClient.post<TwoFactorResult>(config.endpoints.verify2FA, data, {
      requiresAuth: false,  // Pas encore authentifié
    });
  },

  /**
   * Déconnexion utilisateur
   */
  async logout(): Promise<ApiResponse<void>> {
    const config = getAuthConfig();
    return apiClient.post<void>(config.endpoints.logout);
  },

  /**
   * Rafraîchir le token d'accès
   */
  async refreshToken(): Promise<ApiResponse<TokenRefreshResult>> {
    // Passe par apiClient.refreshAccessToken() (pas un post() direct) pour
    // beneficier du verrou single-flight : si un autre appel API a deja
    // declenche un refresh au meme instant, on attend le meme resultat au
    // lieu de faire une 2e requete concurrente sur un refresh token a rotation.
    try {
      const { accessToken } = await apiClient.refreshAccessToken();
      return { succes: true, message: 'Token rafraichi', data: { tokens: { accessToken } } };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Echec du refresh token';
      return { succes: false, message };
    }
  },

  /**
   * Récupérer le profil utilisateur
   */
  async getProfile(): Promise<ApiResponse<UserProfile>> {
    const config = getAuthConfig();
    return apiClient.get<UserProfile>(config.endpoints.getProfile);
  },

  /**
   * Mettre à jour le profil utilisateur
   */
  async updateProfile(data: UpdateProfileData): Promise<ApiResponse<UserProfile>> {
    const config = getAuthConfig();
    return apiClient.put<UserProfile>(config.endpoints.updateProfile, data);
  },

  /**
   * Changer le mot de passe
   */
  async changePassword(data: ChangePasswordData): Promise<ApiResponse<void>> {
    const config = getAuthConfig();
    return apiClient.put<void>(config.endpoints.changePassword, data);
  },

  /**
   * Changer l'email
   */
  async changeEmail(data: ChangeEmailData): Promise<ApiResponse<void>> {
    const config = getAuthConfig();
    return apiClient.put<void>(config.endpoints.changeEmail, data);
  },

  /**
   * Vérifier l'email à l'inscription avec le code reçu par mail
   */
  async verifyEmail(data: { email: string; code: string }): Promise<ApiResponse<void>> {
    const config = getAuthConfig();
    return apiClient.post<void>(config.endpoints.verifyEmail, data, { requiresAuth: false });
  },

  /**
   * Vérifier le changement d'email avec le code
   */
  async verifyEmailChange(data: VerifyEmailChangeData): Promise<ApiResponse<void>> {
    const config = getAuthConfig();
    return apiClient.post<void>(config.endpoints.verifyEmailChange, data);
  },

  /**
   * Supprimer le compte
   */
  async deleteAccount(data: DeleteAccountData): Promise<ApiResponse<DeleteAccountResult>> {
    const config = getAuthConfig();
    return apiClient.delete<DeleteAccountResult>(config.endpoints.deleteAccount, data);
  },
};
